import { animated, useSpring } from '@react-spring/web';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { ChevronLeftBold } from '@sorare/core/src/atoms/icons/ChevronLeftBold';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { LightedBorder } from '@sorare/core/src/atoms/ui/LightedBorder';
import { ANY_SPORT_MARKET_WHEEL_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useEnabledWheelByRarity } from '@sorare/core/src/hooks/useEnabledWheelByRarity';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { getRewardImageUrl } from '@sorare/core/src/lib/rewards';
import { WHEEL_TICKET_BY_RARITY } from '@sorare/core/src/lib/wheelCurrencies';

const Wrapper = styled.div`
  position: relative;
  padding-bottom: var(--double-and-a-half-unit);
`;

const InnerWrapper = styled(Horizontal)`
  align-self: stretch;
  justify-self: stretch;
  position: relative;
  z-index: 1;
`;

const ALL_RARITIES = [
  Rarity.common,
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
] as const;

type Props = {
  rarity: Rarity;
};

export const WheelRaritySwitcher = ({ rarity }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const enabledWheelByRarity = useEnabledWheelByRarity();
  const isLaptopAndAbove = useIsLaptop();
  const { currentUser } = useCurrentUserContext();

  const bumpSpring = useSpring({
    from: { y: -10 },
    to: { y: 0 },
    config: { tension: 300, friction: 10 },
  });

  const availableRarities = ALL_RARITIES.filter(
    r => enabledWheelByRarity[r as keyof typeof enabledWheelByRarity]
  );

  if (availableRarities.length === 0) {
    return null;
  }
  if (!availableRarities.includes(rarity as (typeof ALL_RARITIES)[number])) {
    return null;
  }

  const singleRarity = availableRarities.length === 1;

  const wheelTicketsByRarity = availableRarities.map(
    r =>
      currentUser?.wheelTickets.find(
        currencyBalance =>
          currencyBalance.currency === WHEEL_TICKET_BY_RARITY[r]
      )?.amount || 0
  );
  if (!isLaptopAndAbove) {
    if (singleRarity) {
      return (
        <Horizontal center gap={0.5}>
          <img
            src={getRewardImageUrl('wheel-ticket', rarity)}
            alt={`${rarity} wheel`}
            height={16}
          />
          <LabelL bold>{wheelTicketsByRarity[0]}</LabelL>
        </Horizontal>
      );
    }
    return (
      <TabBar>
        {availableRarities.map((r, index) => {
          return (
            <Tab
              key={r}
              to={generateSportContextPath(ANY_SPORT_MARKET_WHEEL_RARITY, {
                params: {
                  rarity: r,
                },
              })}
            >
              <img
                src={getRewardImageUrl('wheel-ticket', r)}
                alt={`${r} wheel`}
                height={16}
              />
              <LabelL bold>{wheelTicketsByRarity[index]}</LabelL>
            </Tab>
          );
        })}
      </TabBar>
    );
  }

  const rarityIndex = availableRarities.indexOf(
    rarity as (typeof availableRarities)[number]
  );
  return (
    <Wrapper
      style={
        { '--lighted-border-color': getRarityColor(rarity) } as CSSProperties
      }
    >
      <LightedBorder />
      <InnerWrapper gap={1.5}>
        {!singleRarity && (
          <Badge
            badgeContent={
              rarityIndex > 0
                ? wheelTicketsByRarity.at(rarityIndex - 1)
                : undefined
            }
          >
            <IconButton
              size="small"
              color="quaternary"
              disableDebounce
              disabled={rarityIndex === 0}
              replace
              to={
                rarityIndex === 0
                  ? undefined
                  : generateSportContextPath(ANY_SPORT_MARKET_WHEEL_RARITY, {
                      params: {
                        rarity: availableRarities[rarityIndex - 1],
                      },
                    })
              }
            >
              <ChevronLeftBold />
            </IconButton>
          </Badge>
        )}
        <animated.img
          src={getRewardImageUrl('wheel-ticket', rarity)}
          alt={`${rarity} wheel`}
          width={48}
          height={48}
          style={bumpSpring}
        />
        {!singleRarity && (
          <Badge
            badgeContent={
              rarityIndex < availableRarities.length - 1
                ? wheelTicketsByRarity.at(rarityIndex + 1)
                : undefined
            }
          >
            <IconButton
              size="small"
              color="quaternary"
              disableDebounce
              disabled={rarityIndex === availableRarities.length - 1}
              replace
              to={
                rarityIndex === availableRarities.length - 1
                  ? undefined
                  : generateSportContextPath(ANY_SPORT_MARKET_WHEEL_RARITY, {
                      params: {
                        rarity: availableRarities[rarityIndex + 1],
                      },
                    })
              }
            >
              <ChevronRightBold />
            </IconButton>
          </Badge>
        )}
      </InnerWrapper>
    </Wrapper>
  );
};
